import React, { useState, useEffect } from "react";
import UserInfo from "./profile-box";
import EditableUserInfo from "./profile-box-edit";
import { url } from "../../api/index.js";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import { useGetUsernameStatusQuery } from "../../services/rtkApi/authApi";
import {
  useChangePasswordMutation,
  useChangeUsernameMutation,
  useConfirmEmailChangeMutation,
  useGetMeQuery,
  useUpdateEmailMutation,
} from "../../services/rtkApi/authServiceApi";
import type {
  ChangePasswordRequest,
  ChangeUsernameRequest,
  EmailChangeStarted,
  UpdateEmailRequest,
} from "../../services/rtkApi/authServiceApi";
import { toast } from "react-toastify";
import { apiClient } from "../../api/client";
import { tokenService } from "../../services/tokenService";
import { setTokens, signOut } from "../../store/slices/authSlice";
import { logout as oauthLogout } from "../../utils/oauth";
import { useAlert } from "../../ui/components/ConfirmDialog";
import { stepUpErrorMessage } from "../authentication/StepUpCodeField";

interface User {
  username: string;
  email: string;
  userEmoji: string;
  role: string;
  dateSigned?: string;
}

const Profile: React.FC = () => {
  const [editing, setEditing] = useState(false);
  const [user, setUser] = useState<User>();
  const [isLoading, setIsLoading] = useState(false);
  const authUser = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const alert = useAlert();

  const [changePassword] = useChangePasswordMutation();
  const [updateEmail] = useUpdateEmailMutation();
  const [confirmEmailChange] = useConfirmEmailChangeMutation();
  const [changeUsername] = useChangeUsernameMutation();
  const { data: usernameStatus, refetch: refetchUsernameStatus } =
    useGetUsernameStatusQuery(undefined, { skip: !authUser.token });
  const { data: me } = useGetMeQuery(undefined, {
    skip: !authUser.token,
  });
  // set when auth answers step_up_required, so a passwordless account still
  // gets the code fields if /auth/me did not say it has no password
  const [stepUpRequired, setStepUpRequired] = useState(false);
  const hasPassword = me?.hasPassword !== false && !stepUpRequired;
  const passwordLoginAvailable = me?.passwordLoginAvailable !== false;

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    fetchUser();
  }, [authUser]);

  const fetchUser = async () => {
    if (!authUser.username) return;

    try {
      const response = await apiClient.get(
        `${url}/api/edit/userself/${authUser.username}`
      );
      if (response.status !== 200) return;
      setUser(response.data);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        console.log("[Profile] Request failed with 401 - session expired");
        return;
      }
      console.warn("[Profile] Error fetching user:", error);
    }
  };

  // refresh the JWT so Redux + subsequent requests use updated claims
  const refreshTokens = async () => {
    const tokens = await tokenService.refreshSilent();
    if (tokens) {
      dispatch(setTokens({ accessToken: tokens.accessToken }));
    }
  };

  const reportError = (error: any, fallback: string) => {
    if (error?.data?.code === "step_up_required") {
      setStepUpRequired(true);
    }
    toast.error(stepUpErrorMessage(error, fallback), { position: "bottom-right" });
  };

  // the header's sign-out steps: end the backend session, clear local state,
  // then the IdP logout so the next sign-in asks again
  const signOutOfSite = async () => {
    try {
      await apiClient.post(`${url}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.warn("[Profile] Failed to revoke session on server:", error);
    }
    dispatch(signOut());
    oauthLogout(true);
  };

  const handleChangePassword = async (data: ChangePasswordRequest) => {
    setIsLoading(true);
    try {
      await changePassword(data).unwrap();
    } catch (error: any) {
      reportError(error, "Failed to change password");
      setIsLoading(false);
      return;
    }
    // auth ends every session for this account when a password is set or
    // changed, this one included. saying so and signing out here beats a
    // cheerful toast followed by a "session expired" redirect on the next refresh
    await alert({
      title: data.currentPassword ? "Password changed" : "Password set",
      message: "You've been signed out everywhere. Sign in again with your password or an email code.",
    });
    await signOutOfSite();
  };

  const handleChangeEmail = async (
    data: UpdateEmailRequest
  ): Promise<EmailChangeStarted | null> => {
    setIsLoading(true);
    try {
      const result = await updateEmail(data).unwrap();
      // auth switches the address only after the code it mailed there comes back
      if (result?.changeId) {
        return result;
      }
      toast.success("Email updated — check your inbox to verify.", {
        position: "bottom-left",
      });
      await refreshTokens();
      setEditing(false);
      return null;
    } catch (error: any) {
      reportError(error, "Failed to change email");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmEmailChange = async (data: { changeId: string; code: string }) => {
    setIsLoading(true);
    try {
      await confirmEmailChange(data).unwrap();
    } catch (error: any) {
      reportError(error, "That code didn't work. Check the email and try again.");
      setIsLoading(false);
      return;
    }
    // auth ended every session for this account when the address switched
    await alert({
      title: "Email changed",
      message: "You've been signed out. Sign in with your new email.",
    });
    await signOutOfSite();
  };

  const handleChangeUsername = async (data: ChangeUsernameRequest) => {
    setIsLoading(true);
    try {
      await changeUsername(data).unwrap();
      toast.success("Username changed!", { position: "bottom-left" });
      await refreshTokens();
      refetchUsernameStatus();
      setEditing(false);
    } catch (error: any) {
      reportError(error, "Failed to change username");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddEmoji = async (userEmoji: string) => {
    try {
      await apiClient.post(`${url}/api/edit/add-emoji`, { emoji: userEmoji });
      toast.success("Emoji set!", { position: "bottom-left" });
      await fetchUser();
    } catch (error: any) {
      const msg = error?.response?.data?.error || "Failed to set emoji";
      toast.error(msg, { position: "bottom-right" });
    }
  };

  const handleCancel = () => {
    setEditing(false);
  };

  if (!user) {
    return (
      <UserInfo
        username={"..."}
        userEmoji=""
        onAddEmoji={() => {}}
        email={"..."}
        role={"..."}
        dateSigned={"..."}
        onEditButtonClick={() => setEditing(true)}
      />
    );
  }

  return editing ? (
    <EditableUserInfo
      username={user.username}
      email={user.email}
      role={user.role}
      dateSigned={user.dateSigned || "Not available"}
      usernameChanged={usernameStatus?.usernameChanged ?? false}
      hasPassword={hasPassword}
      passwordLoginAvailable={passwordLoginAvailable}
      onChangeUsername={handleChangeUsername}
      onChangeEmail={handleChangeEmail}
      onConfirmEmailChange={handleConfirmEmailChange}
      onChangePassword={handleChangePassword}
      onCancel={handleCancel}
      isLoading={isLoading}
    />
  ) : (
    <UserInfo
      username={user.username}
      email={user.email}
      role={user.role}
      userEmoji={user.userEmoji}
      onAddEmoji={handleAddEmoji}
      dateSigned={user.dateSigned || "Not available"}
      onEditButtonClick={() => setEditing(true)}
    />
  );
};

export default Profile;
